import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { Connections } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Wordmark } from '@accounts/ui';
import { Asking } from './connect/asking';
import { Cause } from './connect/cause';

/**
 * An application's request to use a product for the person (`/connect`), where the service's OAuth
 * authorization (`GET /v1/authorize` with `client_id`) sends the browser.
 *
 * `?ticket=` is a request for the person to decide (`Asking`): it needs the platform session that opened
 * it, so someone not signed in is sent to sign in with this address as where to go on, and comes back
 * to it. `?problem=<code>` is a request the service could not even put to the person, because the
 * application, its description or its redirect could not be trusted, or Accounts could not answer
 * (`Cause`): it is said in either standing, and nothing goes to the application. A screen before the
 * frame: a problem is centred like signing in, and the choice is the shared `Page` with its summary
 * pinned beside it (2026-10-31); the static host refuses to frame it (`public/_headers`).
 */
export function Connect() {
	const { standing } = usePresence();
	const [parameters] = useSearchParams();
	const location = useLocation();
	const problem = parameters.get('problem');

	// A request to decide needs a session; the sign-in address carries this one back
	if (!problem && standing !== 'in') return <Navigate to={`/signin?to=${encodeURIComponent(`${location.pathname}${location.search}`)}`} replace />;

	if (problem) {
		return (
			<div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-6 px-4 py-10">
				<Wordmark label="Accounts" />
				<Cause cause={Connections.cause(problem)} signed={standing === 'in'} />
			</div>
		);
	}
	// The choice and what it leaves stand side by side on a wide screen: the frame is the shared page's
	return (
		<div className="permission-frame mx-auto w-full max-w-6xl">
			<div className="permission-mark">
				<Wordmark label="Accounts" />
			</div>
			<Asking ticket={parameters.get('ticket') ?? ''} />
		</div>
	);
}
